import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { DailyReportService } from '../../../../daily-report.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-cle-edit-labor-form',
  templateUrl: './cle-edit-labor-form.component.html',
  styleUrls: ['./cle-edit-labor-form.component.scss'],
})
export class CleEditLaborFormComponent implements OnInit, OnDestroy {
  @Input() data: any;

  subContractorOptions = [];
  form = new FormGroup<any>({
    sub_contractor: new FormControl(null, [Validators.required]),
    quantity: new FormControl(null),
    hours: new FormControl(null),
    comment: new FormControl(null),
  });
  errorMessages: any = {};
  errorMessageMap: any = {
    sub_contractor: { required: 'Contractor is required' },
  };
  formSubscription: Subscription = new Subscription();
  private allContractors: any[] = [];
  private reportContractors: any[] = [];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private dailyReportService: DailyReportService,
    private commonService: CommonService,
  ) {
  }

  ngOnInit() {
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.allContractors = project?.sub_contractors || [];
        this.buildContractorOptions();
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.reportContractors = report?.reportDetails?.sub_contractors || [];
        this.buildContractorOptions();
        const contractorId = this.data?.sub_contractor_id || this.data?.sub_contractor?.id || this.data?.sub_contractor;
        this.form.controls['sub_contractor'].setValue(contractorId, { emitEvent: false });
      });
    this.form.controls['hours'].setValue(this.data?.duration || null, { emitEvent: false });
    this.form.controls['comment'].setValue(this.data?.comment || null, { emitEvent: false });

    if (this.data?.source_type === 'genericPersonnel') {
      this.form.controls['quantity'].setValue(this.data?.quantity || null, { emitEvent: false });
    }

    const initialValues = JSON.stringify(this.form.getRawValue());
    this.formSubscription = this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        const currentValues = JSON.stringify(this.form.getRawValue());
        if (currentValues !== initialValues) {
          this.projectsService.isAnyFormDirty.next(true);
          this.formSubscription?.unsubscribe();
        }
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.validate()) {
          this.save();
        }
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.delete();
      });
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  private buildContractorOptions() {
    this.subContractorOptions = this.reportContractors.map(sc => {
      const projectContractor = this.allContractors.find(c => c.value === sc.sub_contractor?.id);
      return {
        value: sc.sub_contractor?.id,
        label: sc.sub_contractor?.name,
        id: sc.sub_contractor?.id,
        tag: projectContractor?.tag || null,
      };
    }).sort((a, b) => {
      const aPrime = a.tag ? 1 : 0;
      const bPrime = b.tag ? 1 : 0;
      return bPrime - aPrime;
    });
  }

  validate(): boolean {
    if (this.form.invalid) {
      this.checkForErrors();
      return false;
    }
    return true;
  }

  save() {
    const formValues = this.form.getRawValue();
    const laborEntry = {
      ...this.data,
      sub_contractor_id: formValues.sub_contractor,
      duration: formValues.hours || 0,
      comment: formValues.comment || null,
    };

    if (this.data?.source_type === 'genericPersonnel') {
      laborEntry.quantity = formValues.quantity || 0;
    }

    this.dailyReportService.save.next({
      type: 'labor',
      data: [laborEntry],
    });
  }

  delete() {
    this.dailyReportService.delete.next({
      type: 'labor',
      id: this.data?.id,
    });
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
